import PropTypes from "prop-types";
import { useMemo } from "react";
import styled, { css } from "styled-components";
import {
  ArrowUpIcon,
  ColorPalette,
  FontColors,
  YukaThemeProvider,
  ArrowDownIcon,
  Fonts,
  TruncatedText,
} from "yuka";

import { useLastWeekPortfolioValue, usePortfolioValue } from "./hooks";

import { percentFormat } from "../utils/displayFormatUtils";

const TruncatedPortfolioName = styled(TruncatedText)`
  max-width: 400px;
`;

const StyledCounter = styled.div`
  ${FontColors.theme80}

  display: flex;
  justify-content: center;
  align-items: center;
  line-height: 16px;
  border-radius: 8px;
  padding: 0 8px;
  height: 16px;
  ${(props) => {
    if (props.$percentChange) {
      if (props.$percentChange > 0) {
        return css`
          background: ${props.theme.colors.green};
        `;
      }
      if (props.$percentChange < 0) {
        return css`
          background: ${props.theme.colors.red};
        `;
      }
    }
    return css`
      background: ${ColorPalette.white15};
    `;
  }}
`;

const PortfolioTabContent = ({ portfolioId, name }) => {
  const portfolioValue = usePortfolioValue(portfolioId);
  const lastWeekPortfolioValue = useLastWeekPortfolioValue(portfolioId);

  const percentChange = useMemo(() => {
    if (portfolioValue === null || lastWeekPortfolioValue === null) {
      return null;
    }
    if (lastWeekPortfolioValue === 0) {
      return null; // Infinity percent
    }
    return (portfolioValue - lastWeekPortfolioValue) / lastWeekPortfolioValue;
  }, [portfolioValue, lastWeekPortfolioValue]);

  const ArrowIcon = useMemo(() => {
    if (percentChange === null) {
      return null;
    }
    return percentChange >= 0 ? ArrowUpIcon : ArrowDownIcon;
  }, [percentChange]);

  const percentDisplay = useMemo(() => {
    if (percentChange !== null) {
      return (
        <YukaThemeProvider
          theme={{ mode: percentChange === 0 ? "dark" : "light" }}
        >
          <Fonts.Caption2theme80>
            {percentFormat(Math.abs(percentChange) * 100, 2)}
          </Fonts.Caption2theme80>
        </YukaThemeProvider>
      );
    }
    return (
      <YukaThemeProvider theme={{ mode: "dark" }}>
        <Fonts.Caption2theme30>--</Fonts.Caption2theme30>
      </YukaThemeProvider>
    );
  }, [percentChange]);

  return (
    <>
      <TruncatedPortfolioName>{name}</TruncatedPortfolioName>
      <StyledCounter $percentChange={percentChange}>
        {ArrowIcon && (
          <ArrowIcon
            color={
              percentChange === 0 ? ColorPalette.white50 : ColorPalette.black50
            }
          />
        )}
        {percentDisplay}
      </StyledCounter>
    </>
  );
};

PortfolioTabContent.propTypes = {
  portfolioId: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
};

export default PortfolioTabContent;
